import type { SixDetailFilterKey, FileSortDir, FileSortKey } from '@/types/SixDetail.types';
import { buildQueryString } from '@/utils/buildQuery';

const createCacheBustValue = (): string => crypto.randomUUID();

export const sixEndpoints = {
  getSixes: '/private/v2/sixes',
  createSixModalInfo: '/private/v1/create-six-modal-information',
  validateSixName: '/private/v1/sixes/validate-name',
  createSix: '/private/v1/sixes',
  searchSixes: '/private/v1/sixes/search',

  getSix: (sixId: string, params?: { filter?: readonly SixDetailFilterKey[] }) =>
    `/private/v1/sixes/${sixId}${buildQueryString({
      filter: params?.filter?.join(','),
    })}`,

  getSixFiles: (
    sixId: string,
    params?: {
      filter?: readonly SixDetailFilterKey[];
      search?: string;
      path?: string;
      sortKey?: FileSortKey;
      sortDir?: FileSortDir;
    }
  ) =>
    `/private/v2/sixes/${sixId}/files${buildQueryString({
      filter: params?.filter?.join(','),
      search: params?.search,
      path: params?.path,
      sortKey: params?.sortKey,
      sortDir: params?.sortDir,
    })}`,
  getSixActivity: (sixId: string) =>
    `/private/v1/sixes/${sixId}/activity${buildQueryString({ _: createCacheBustValue() })}`,
  getSixParticipants: (sixId: string) => `/private/v2/sixes/${sixId}/participants`,
  uploadSixFiles: (sixId: string) => `/private/v1/sixes/${sixId}/files`,
  makeFileAvailableToHap: (sixId: string) => `/private/v1/sixes/${sixId}/file`,
  createSixFolder: (sixId: string) => `/private/v1/sixes/${sixId}/folders`,
  deleteFolder: (sixId: string) => `/private/v1/sixes/${sixId}/folders`,
  updateSixParticipants: (sixId: string) => `/private/v2/sixes/${sixId}/participants`,
  updateSix: (sixId: string) => `/private/v1/sixes/${sixId}`,
  deleteSix: (sixId: string) => `/private/v1/sixes/${sixId}`,
  toggleFavoriteSix: (sixId: string) => `/private/v1/sixes/${sixId}/favorite`,
  toggleFileFavorite: (sixId: string, fileId: string) =>
    `/private/v1/sixes/${sixId}/files/${fileId}/favorite`,
  deleteSixFile: (sixId: string, fileId: string) => `/private/v1/sixes/${sixId}/file/${fileId}`,
  markFileDownloaded: (sixId: string, fileId: string) =>
    `/private/v1/sixes/${sixId}/file/${fileId}/downloaded`,
  moveItems: (sixId: string) => `/private/v1/sixes/${sixId}/files/move`,
  getMissingFileKeys: () =>
    `/private/v1/sixes/missing-file-keys${buildQueryString({ _: createCacheBustValue() })}`,
  getDeletedFiles: () =>
    `/private/v1/sixes/deleted-files${buildQueryString({ _: createCacheBustValue() })}`,
  addFileKey: (sixId: string, fileId: string) =>
    `/private/v1/sixes/${sixId}/${fileId}/add-file-key`,
} as const;

export type SixEndpointKey = keyof typeof sixEndpoints;
